import React, { useEffect, useState } from 'react'
import { Typography, Box, Card, SxProps, Link, Tooltip, Button, Skeleton, Divider, Container, CardHeader, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material'
import 'firebase/auth'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import CloseIcon from '@mui/icons-material/Close'
import { gSx } from '../../styles/Theme'

import { Media } from 'service/media'
import { LoadingButton } from '@mui/lab'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Path } from 'routes'
import { useNavigate } from 'react-router-dom'
import ContainerSmall from 'components/ContainerSmall'
import ScrollToAnchor from 'components/ScrollToAnchor'
import { getPreciseFpConfig, disconnectPrecisefp, precisefpOAuth } from 'service/integrations/precisefpApi'
import { retryStopOnlyNotFound } from 'hooks/hookHelpers'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Api } from 'service/api'
import { ReferralEnum } from 'client'
import { getErrorMessage } from 'utils'
import { logErr, logInfo, logWarn } from 'log'

export default function PrecisefpSettings() {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | undefined>()
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)

  async function onSignupClick() {
    try {
      const api = await Api()
      await api.referrals.log(ReferralEnum.PRECISEFP_SIGNUP)
    } catch (e) {
      const err = getErrorMessage(e)
      logErr('PreciseFP onSignupClick', { err })
    }
  }

  async function onConnectClick() {
    try {
      const api = await Api()
      await api.referrals.log(ReferralEnum.PRECISEFP_CONTACT)
      setDialogOpen(true)
    } catch (e) {
      const err = getErrorMessage(e)
      logErr('PreciseFP onConnectClick', { err })
    }
  }

  useEffect(() => {
    logInfo('Precisefp Settings Screen')
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['getPreciseFpConfig'], queryFn: getPreciseFpConfig,
    retry: retryStopOnlyNotFound
  })

  async function onSignIn() {
    setBusy(true)
    logInfo('PreciseFp Sign In Pressed')
    await precisefpOAuth()
    setBusy(false)
  }

  async function signout() {
    logInfo('Precisefp Signout Pressed')
    setErr(undefined)
    setBusy(true)
    const res = await disconnectPrecisefp()
    if (res instanceof Error) {
      setErr(res.message)
    }
    else {
      queryClient.resetQueries({ queryKey: ['getPreciseFpConfig'] })
    }
    setBusy(false)
  }

  return (
    <ContainerSmall>
      <Button
        onClick={() => navigate(Path.Integrations)}
        startIcon={<ArrowBackIcon />}
      >
        Integrations
      </Button>

      <Typography variant='h3'>PreciseFP Integration</Typography>
      <Divider sx={{ marginY: '2vh ' }} />

      <Link href="https://precisefp.com" rel='noopener' target='_blank' underline="hover">
        <Box
          component="img"
          sx={icon}
          src={Media.precisefpLogo}
          alt="PreciseFP"
        />
      </Link>

      <Box sx={{ padding: '1vh ' }} />

      <Typography>Connect FinMate AI with PreciseFP to automatically generate and transfer detailed financial records from your client meetings.</Typography>
      <Box sx={{ padding: '1vh ' }} />
      <Typography>Save time and reduce errors by eliminating manual data entry.</Typography>

      <Box sx={{ padding: '1vh ' }} />

      <Card>

        {isLoading ? (
          <Skeleton variant="rectangular" width={'100%'} height={100} />
        ) : (
          data ? (
            <Box sx={gSx.RowBetween}>
              <Typography>Signed In as {data?.user?.first_name} {data?.user?.last_name}, {data?.user?.email}</Typography>

              <Tooltip title="Disconnect PreciseFP">
                <LoadingButton
                  aria-label='Disconnect PreciseFP'
                  onClick={signout}
                  disabled={busy}
                  loading={busy}
                >
                  <CloseIcon />
                </LoadingButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={sxConnect}>
              <LoadingButton
                variant='contained' onClick={onSignIn}
                loading={busy}
                disabled={busy}
                sx={{ width: 150 }}
              >
                Connect
              </LoadingButton>
              <Box sx={{ padding: '1vh ' }} />
              <Typography variant="body2" color="text.secondary">
                Don&apos;t have a PreciseFP account?{' '}
                <Link
                  href="https://www.precisefp.com/signup"
                  onClick={onSignupClick}
                  underline="hover"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                >
                  Start a 14-day free trial
                  <OpenInNewIcon color="primary" sx={{ paddingLeft: "3px", fontSize: 14 }} />
                </Link>
              </Typography>
              <Box sx={{ padding: '2px ' }} />
              <Typography variant="body2" color="text.secondary">
                <Link
                  component="button"
                  variant="body2"
                  onClick={onConnectClick}
                  underline="hover"
                  color="primary"
                  sx={{ cursor: 'pointer', textAlign: 'left', p: 0 }}
                >
                  Get in touch with PreciseFP
                </Link>
              </Typography>
            </Box>
          )
        )
        }
        {err && <Typography sx={{ color: 'tomato' }}>
          {err}
        </Typography>
        }
      </Card >

      {/* <Button target="_blank" rel="noopener"
        href="https://docs.finmate.ai/docs/category/precisefp"
        startIcon={<TextSnippetIcon />}
      >
        PreciseFP Docs
      </Button> */}
      {/* Success dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          PreciseFP Contact Request Received
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            We will get you in contact with a member from PreciseFP.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary" autoFocus>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <ScrollToAnchor />
    </ContainerSmall >
  )
}

const icon: SxProps = {
  width: 200,
  marginRight: 2
}

const sxConnect: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}